/**
 * HUD — top-of-screen status bar (festival time, current zone, player count,
 * now-playing across all stages) + bottom emote bar.
 */

import { html, render } from 'lit-html';
import { useUI } from '@/state/store';
import { AnimState } from '@shared/protocol';

export interface HUDCallbacks {
  onEmote: (anim: AnimState) => void;
  onOpenChat: () => void;
  onOpenMap: () => void;
  onToggleMultiplayer: () => void;
  onToggleCamera: () => void;
  getCameraMode: () => 'first' | 'third';
}

const EMOTE_KEYS: { key: string; anim: AnimState; label: string }[] = [
  { key: '1', anim: AnimState.Waving, label: '👋' },
  { key: '2', anim: AnimState.Dancing, label: '💃' },
  { key: '3', anim: AnimState.Sitting, label: '🪑' },
  { key: '4', anim: AnimState.Headbang, label: '🤘' },
  { key: '5', anim: AnimState.Peace, label: '✌️' },
  { key: '6', anim: AnimState.Clap, label: '👏' },
  { key: '7', anim: AnimState.HandsUp, label: '🙌' },
  { key: '8', anim: AnimState.Lying, label: '😴' },
  { key: '9', anim: AnimState.Idle, label: '🧍' }
];

export class HUD {
  private container: HTMLDivElement;

  constructor(private cb: HUDCallbacks) {
    const root = document.getElementById('ui-root')!;
    this.container = document.createElement('div');
    this.container.className = 'hud';
    root.appendChild(this.container);

    useUI.subscribe(() => this.render());
    this.render();
  }

  private render(): void {
    const s = useUI.getState();
    const tpl = html`
      <div class="hud-top">
        <div class="hud-pill hud-time">${s.festivalTimeLabel || '...'}</div>
        <div class="hud-pill hud-zone">${zoneLabel(s.currentZone)}</div>
        <button
          class="hud-pill hud-btn ${s.connected ? 'hud-live' : ''}"
          @click=${() => this.cb.onToggleMultiplayer()}
          title="${s.connected ? 'Click to go solo' : 'Click to meet other people on the farm'}"
        >
          <span class="dot ${s.connected ? 'on' : 'off'}"></span>
          ${s.connected ? `${s.playerCount} live` : 'Go live'}
        </button>
        <button
          class="hud-pill hud-btn"
          @click=${() => this.cb.onToggleCamera()}
          title="Toggle first/third person (V)"
        >${this.cb.getCameraMode() === 'first' ? 'FPV' : '3rd'}</button>
        <button class="hud-pill hud-btn" @click=${() => this.cb.onOpenMap()}>Map (M)</button>
      </div>

      <div class="hud-now">
        ${s.nowPlaying.slice(0, 6).map(
          (np) => html`
            <div class="np">
              <div class="np-stage">${stageLabel(np.stageId)}</div>
              <div class="np-artist">${np.artist}</div>
            </div>
          `
        )}
      </div>

      <div class="hud-bottom">
        <button class="hud-chat-btn" @click=${() => this.cb.onOpenChat()}>
          Chat (T)
        </button>
        <div class="hud-emotes">
          ${EMOTE_KEYS.map(
            (e) => html`
              <button class="hud-emote" @click=${() => this.cb.onEmote(e.anim)}>
                <span class="hud-emote-label">${e.label}</span>
                <span class="hud-emote-key">${e.key}</span>
              </button>
            `
          )}
        </div>
      </div>
    `;
    render(tpl, this.container);
  }
}

function zoneLabel(zoneId: string | null): string {
  if (!zoneId) return '';
  const map: Record<string, string> = {
    'arch': 'The Arch',
    'centeroo-core': 'Centeroo',
    'what-stage': 'What Stage',
    'which-stage': 'Which Stage',
    'this-that-tents': 'This/That Tent',
    'other-stage': 'The Other Stage',
    'woods': 'Where in the Woods',
    'outeroo-pods': 'Outeroo',
    'ferris': 'Ferris Wheel'
  };
  return map[zoneId.replace(/-\d+$/, '')] || zoneId;
}

function stageLabel(stageId: string): string {
  const map: Record<string, string> = {
    'what-stage': 'What',
    'which-stage': 'Which',
    'this-tent': 'This',
    'that-tent': 'That',
    'other-stage': 'Other',
    'woods': 'Woods'
  };
  return map[stageId] ?? stageId;
}
